import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = [
  { value: 'great', emoji: '😄', label: 'Great', color: '#4CAF50' },
  { value: 'good', emoji: '🙂', label: 'Good', color: '#8BC34A' },
  { value: 'okay', emoji: '😐', label: 'Okay', color: '#FFC107' },
  { value: 'bad', emoji: '😔', label: 'Bad', color: '#FF9800' },
  { value: 'awful', emoji: '😢', label: 'Awful', color: '#F44336' },
];

const MoodTracker = ({ patient, date }) => {
  const [selectedMood, setSelectedMood] = useState(null);
  const [todayMood, setTodayMood] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (patient?.id && date) {
      fetchTodayMood();
    }
  }, [patient?.id, date]);

  const fetchTodayMood = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/moods/${patient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const today = response.data.find(m => m.date === date);
      if (today) {
        setTodayMood(today);
        setSelectedMood(today.mood);
      } else {
        setTodayMood(null);
        setSelectedMood(null);
      }
    } catch (error) {
      console.error('Error fetching mood:', error);
    }
  };

  const handleMoodSelect = async (mood) => {
    if (loading) return;
    
    setLoading(true);
    setSelectedMood(mood);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/moods`, {
        patient_id: patient.id,
        date: date,
        mood: mood
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Mood logged!');
      fetchTodayMood();
    } catch (error) {
      toast.error('Failed to log mood');
      setSelectedMood(todayMood?.mood || null);
    } finally {
      setLoading(false);
    }
  };

  if (!patient) return null;

  return (
    <div className="card h-full" data-testid="mood-tracker">
      <h3 className="text-base sm:text-lg font-semibold mb-3" style={{ color: '#A2DAD3' }}>
        How is {patient.name} feeling?
      </h3>
      
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
        {MOODS.map((mood) => (
          <motion.button
            key={mood.value}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleMoodSelect(mood.value)}
            disabled={loading}
            className={`flex flex-col items-center p-2 sm:p-3 rounded-xl transition-all ${
              selectedMood === mood.value 
                ? 'ring-2 ring-offset-2' 
                : 'hover:bg-gray-50'
            }`}
            style={{
              backgroundColor: selectedMood === mood.value ? `${mood.color}20` : 'transparent',
              ringColor: selectedMood === mood.value ? mood.color : 'transparent',
              minWidth: '48px'
            }}
            data-testid={`mood-${mood.value}`}
          >
            <span className="text-xl sm:text-2xl md:text-3xl mb-0.5 sm:mb-1">{mood.emoji}</span>
            <span className="text-[10px] sm:text-xs font-medium" style={{ 
              color: selectedMood === mood.value ? mood.color : '#666' 
            }}>
              {mood.label}
            </span>
          </motion.button>
        ))}
      </div>
      
      {todayMood && (
        <p className="text-[10px] sm:text-xs text-gray-500 mt-2 sm:mt-3 text-center">
          Updated: {new Date(todayMood.created_at).toLocaleTimeString()}
        </p>
      )}
    </div>
  );
};

export default MoodTracker;
